// Fase 17.3, Plano 04 — cliente HTTP e utilitários da tela "Importar roteiros do CargaMáquina".
import type {
  ArquivoImportacaoCargaMaquinaDto,
  CelulaCodigoOverrideDto,
  ExcecaoImportacaoRoteiroDto,
  ImportacaoRoteiroResumoDto,
  MotivoExcecaoImportacaoRoteiro,
  PreviaImportacaoCargaMaquinaCelulaDto,
  PreviaImportacaoCargaMaquinaDto,
  ResultadoImportacaoCargaMaquinaDto,
} from '@pieltec/contracts';
import { apiFetch, apiMutate } from '../csrf-fetch';
import { lerErroApi } from '../vendas/erro-api';

export const ROTULO_MOTIVO_EXCECAO: Readonly<Record<MotivoExcecaoImportacaoRoteiro, string>> = {
  SEM_MATERIAL: 'Peça sem cadastro de material',
  BASE_AUSENTE_NO_ARQUIVO: 'Peça base ausente no arquivo',
  SEQUENCIA_COM_LACUNA: 'Sequência de etapas com lacuna',
  SERVICO_INVALIDO: 'Serviço inválido',
  VARIANTE_SERVICO_FINAL: 'Etapa em Finalizados PCP',
  CELULA_DESCONHECIDA: 'Célula desconhecida',
  SERVICO_SEM_CELULA: 'Serviço sem célula',
  CODIGO_LEGADO_DUPLICADO: 'Código legado duplicado',
  TINTA_CITADA_MULTIPLA: 'Descrição cita mais de uma tinta',
  TINTA_CITADA_INELEGIVEL: 'Tinta citada não elegível: confira Situação, Tipo de tinta, Cor e unidade em Cadastros → Materiais → Editar Selecionados',
  LINHA_COM_ERRO: 'Linha com erro (dados técnicos)',
};

const ROTA_IMPORTACOES = '/api/v1/roteiros/importacoes';
const ERRO_PADRAO = 'Não foi possível processar o arquivo. Tente novamente.';

/** Lê o arquivo como Base64 puro (sem o prefixo `data:...;base64,`). */
export function lerArquivoComoBase64(arquivo: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => {
      const resultado = typeof leitor.result === 'string' ? leitor.result : '';
      resolve(resultado.slice(resultado.indexOf(',') + 1));
    };
    leitor.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
    leitor.readAsDataURL(arquivo);
  });
}

/** Nome e conteúdo lidos do MESMO arquivo, no mesmo instante: prévia e confirmação nunca misturam um nome com o conteúdo de outro (P3-WR-03). */
export interface ArquivoLido {
  readonly nome: string;
  readonly base64: string;
}

export async function lerArquivo(arquivo: File): Promise<ArquivoLido> {
  const nome = arquivo.name;
  return { nome, base64: await lerArquivoComoBase64(arquivo) };
}

async function chamar<T>(caminho: string, corpo: unknown): Promise<T> {
  const resposta = await apiMutate(`${ROTA_IMPORTACOES}/${caminho}`, { method: 'POST', body: corpo });
  if (!resposta.ok) {
    const erro = await lerErroApi(resposta, ERRO_PADRAO);
    throw new Error(resposta.status === 403 ? 'Você não tem permissão para importar roteiros.' : erro.mensagem);
  }
  return (await resposta.json()) as T;
}

export function previaImportacaoCargaMaquina(corpo: ArquivoImportacaoCargaMaquinaDto): Promise<PreviaImportacaoCargaMaquinaDto> {
  return chamar<PreviaImportacaoCargaMaquinaDto>('cargamaquina/previa', corpo);
}

export const REGEX_CODIGO_CELULA = /^[A-Z0-9][A-Z0-9_-]{0,19}$/;

/** Mesma regra do DTO da API: letras maiúsculas, números, `_` ou `-`, até 20 caracteres. */
export function codigoCelulaValido(codigo: string): boolean {
  return REGEX_CODIGO_CELULA.test(codigo);
}

type CelulaDaPrevia = Pick<PreviaImportacaoCargaMaquinaCelulaDto, 'nomeArquivo' | 'codigo'> & Partial<Pick<PreviaImportacaoCargaMaquinaCelulaDto, 'situacao'>>;

/** Célula que já existe em Cadastros mantém o código lá cadastrado: o campo fica desabilitado e a prévia não o corrige (P3-WR-07). */
export function celulaEditavel(celula: Partial<Pick<PreviaImportacaoCargaMaquinaCelulaDto, 'situacao'>>): boolean {
  return celula.situacao !== 'EXISTENTE';
}

/** Código em edição da célula (caixa alta, sem espaços nas pontas); cai no proposto pela prévia quando ainda não foi editado. */
export function codigoEditado(celula: Pick<PreviaImportacaoCargaMaquinaCelulaDto, 'nomeArquivo' | 'codigo'>, edicoes: Readonly<Record<string, string>>): string {
  return (edicoes[celula.nomeArquivo] ?? celula.codigo).trim().toUpperCase();
}

/** Alguma célula NOVA com código fora do formato da API? As que já existem não entram: o PCP não tem como corrigi-las nesta tela. */
export function temCodigoInvalido(celulasPrevia: readonly CelulaDaPrevia[], edicoes: Readonly<Record<string, string>>): boolean {
  return celulasPrevia.some((celula) => celulaEditavel(celula) && !codigoCelulaValido(codigoEditado(celula, edicoes)));
}

/** Confirmação também é permitida quando a única novidade do arquivo são vínculos automáticos de tinta. */
export function previaTemNovidade(previa: PreviaImportacaoCargaMaquinaDto): boolean {
  return previa.pecas.novas > 0
    || previa.servicos.some((servico) => servico.situacao === 'NOVO')
    || previa.celulas.some((celula) => celula.situacao !== 'EXISTENTE')
    || previa.modelos.some((modelo) => modelo.situacao === 'NOVO')
    || previa.coresSugeridas > 0
    || previa.tintasDaPeca.vinculadas > 0;
}

/** Só as células editáveis cujo código editado difere do proposto na prévia; códigos em caixa alta e sem espaços nas pontas. */
export function overridesAlterados(
  celulasPrevia: readonly CelulaDaPrevia[],
  edicoes: Readonly<Record<string, string>>,
): CelulaCodigoOverrideDto[] {
  const alterados: CelulaCodigoOverrideDto[] = [];
  for (const celula of celulasPrevia) {
    if (!celulaEditavel(celula)) continue;
    const editado = edicoes[celula.nomeArquivo];
    if (editado === undefined) continue;
    const codigo = editado.trim().toUpperCase();
    if (codigo !== celula.codigo) alterados.push({ nomeArquivo: celula.nomeArquivo, codigo });
  }
  return alterados;
}

const INICIO_FORMULA = /^[=+\-@\t\r]/;

export function celulaCsv(valor: string): string {
  // Planilhas executam células que começam com = + - @: o apóstrofo força texto (T-17.3-04-05).
  const seguro = INICIO_FORMULA.test(valor) ? `'${valor}` : valor;
  return /[;"\r\n]/.test(seguro) ? `"${seguro.replaceAll('"', '""')}"` : seguro;
}

/** CSV de exceções para abrir no Excel: separador `;`, UTF-8 com BOM e proteção contra injeção de fórmula. */
export function excecoesParaCsv(excecoes: readonly ExcecaoImportacaoRoteiroDto[]): string {
  const linhas = [
    'motivo;codigo;detalhe;bloqueante',
    ...excecoes.map((excecao) => [
      ROTULO_MOTIVO_EXCECAO[excecao.motivo], excecao.codigoBase, excecao.detalhe, excecao.bloqueante ? 'sim' : 'não',
    ].map(celulaCsv).join(';')),
  ];
  return `﻿${linhas.join('\r\n')}\r\n`;
}

/** Baixa um texto como arquivo, sem Web Storage: Blob + URL temporária. */
export function baixarTexto(nomeArquivo: string, conteudo: string, tipo = 'text/csv;charset=utf-8'): void {
  const url = URL.createObjectURL(new Blob([conteudo], { type: tipo }));
  const ancora = document.createElement('a');
  ancora.href = url;
  ancora.download = nomeArquivo;
  document.body.appendChild(ancora);
  ancora.click();
  ancora.remove();
  URL.revokeObjectURL(url);
}

export function confirmarImportacaoCargaMaquina(corpo: ArquivoImportacaoCargaMaquinaDto): Promise<ResultadoImportacaoCargaMaquinaDto> {
  return chamar<ResultadoImportacaoCargaMaquinaDto>('cargamaquina/confirmar', corpo);
}

export function listarImportacoes(): Promise<ImportacaoRoteiroResumoDto[]> {
  return apiFetch<ImportacaoRoteiroResumoDto[]>(`${ROTA_IMPORTACOES}?tipo=CARGAMAQUINA`);
}

export function excecoesDaImportacao(id: string): Promise<ExcecaoImportacaoRoteiroDto[]> {
  return apiFetch<ExcecaoImportacaoRoteiroDto[]>(`${ROTA_IMPORTACOES}/${encodeURIComponent(id)}/excecoes`);
}
